<template>
  <div class="few-more-information-content">
    <h3 class="mb-4">
      {{ __('Só mais algumas informações importantes para melhorar ainda mais a sua experiência no Salesflow!') }}
    </h3>

    <form @submit.prevent="advance" class="few-more-information-form">
      <div class="few-more-information-form-group">
        <label for="company">{{ __('Nome da empresa') }}</label>
        <input
          id="company"
          class="text-input"
          type="text"
          v-model="state.company"
          minlength="2"
          maxlength="50"
        />
        <span v-if="hasErrorsByKey('company')" class="errors">
          <p v-for="error in getErrorsByKey('company')" class="text-danger mb-0">{{ error }}</p>
        </span>
      </div>

      <div class="few-more-information-form-group">
        <label for="segment">{{ __('Segmento') }}</label>
        <InputDropdown
          id="segment"
          :placeholder="__('Selecione')"
          truncate-at="40"
          :items="state.segment.options"
          :selected="state.segment.selected.name"
          @select="(option) => setSelected(option, 'segment')"
        />
      </div>

      <div class="few-more-information-form-group">
        <label for="employees">{{ __('Quantidade de funcionários') }}</label>
        <InputDropdown
          id="employees"
          :placeholder="__('Quantidade de funcionários')"
          :items="state.employees.options"
          :selected="state.employees.selected.name"
          @select="(option) => setSelected(option, 'employees')"
        />
      </div>

      <div class="few-more-information-form-group">
        <label for="users">{{ __('Quantas pessoas utilizariam o Salesflow?') }}</label>
        <BButtonGroup class="button-group">
          <button
            type="button"
            class="button first"
            :class="{ 'active': state.numberOfUsers === '1' }"
            @click="setNumberOfUsers('1')"
          >1</button>
          <button
            type="button"
            class="button"
            :class="{ 'active': state.numberOfUsers === '2-5' }"
            @click="setNumberOfUsers('2-5')"
          >2-5</button>
          <button
            type="button"
            class="button"
            :class="{ 'active': state.numberOfUsers === '6-10' }"
            @click="setNumberOfUsers('6-10')"
          >6-10</button>
          <button
            type="button"
            class="button last"
            :class="{ 'active': state.numberOfUsers === '11-50' }"
            @click="setNumberOfUsers('11-50')"
          >11-50</button>
        </BButtonGroup>
      </div>

      <div class="invite-content" v-show="state.isInviteOpen">
        <h4>{{ __('Convide os primeiros usuários') }}</h4>
        <h5>{{ __('Você poderá convidar mais usuários após finalizar seu cadastro.') }}</h5>
        <div class="invite-group" v-for="(invite, index) in state.invitations" :key="index">
          <div>
            <input
              type="text"
              class="text-input"
              :placeholder="__('Digite o e-mail do convidado')"
              v-model="invite.email"
            />
            <span v-if="inviteHasErrorsByIndex(index)" class="errors">
              <p v-for="error in getInviteErrorsByIndex(index)" class="text-danger mb-0">{{ error }}</p>
            </span>
          </div>
          <InputDropdown
            id="role"
            :items="state.role.options"
            :selected="invite.role"
            @select="(option) => setRoleToInvite(option, index)"
          />
          <div
            v-show="state.invitations.length > 1"
            @click="removeInvite(index)"
          >
            <Trash :size="16" color="#71767D" style="cursor: pointer;"/>
          </div>
        </div>
        <span
          @click="addInvite"
          v-if="state.invitations.length <= 2"
        >+ {{ __('Adicionar mais') }}</span>
      </div>

      <button class="w-100 salesflow-btn salesflow-btn-primary">
        {{ __('Avançar') }}
      </button>
    </form>
  </div>
</template>

<script>
import { errorHandling } from '../../mixins/error-handling';
import InputDropdown from '../input/Dropdown.vue';
import { BButtonGroup } from 'bootstrap-vue';
import Trash from '../icon/Trash.vue';

export default {
  components: {
    InputDropdown,
    BButtonGroup,
    Trash
  },

  mixins: [errorHandling],

  props: {
    state: Object,
  },

  computed: {
    data() {
      return {
        company: this.state.company,
        segment: this.state.segment.selected.name,
        employees: this.state.employees.selected.name,
        users: this.state.numberOfUsers,
      }
    }
  },

  methods: {
    setSelected (option, state) {
      this.state[state].selected = option;
    },

    setRoleToInvite (option, index) {
      this.state.invitations[index].role = option.name;
    },

    setNumberOfUsers(value) {
      this.state.numberOfUsers = value;

      if(value === '1') {
        this.state.isInviteOpen = false;
        this.state.invitations = [{ email: '', role: 'Comercial' }];
      }  else {
        this.state.isInviteOpen = true;
      }
    },

    addInvite() {
      if(this.state.invitations.length === 3) return;
      this.state.invitations.push({
        email: '',
        role: 'Comercial'
      })
    },

    removeInvite(index) {
      this.state.invitations.splice(index, 1);
    },

    advance () {
      const validation = this.validateFields();
      if (validation.error) {
        return Alert.error(this.__(validation.error));
      }
      Alert.close();

      // remover invites com campo email vazio
      const validInvitations = this.state.invitations.filter(invitation => invitation.email.length > 0);
      let data = this.data;
      data.invitations = validInvitations

      this.$emit('success', this.state);
    },

    validateFields () {
      if (
        !this.data.company
        || !this.data.segment
        || !this.data.employees
        || !this.data.users
      ) {
        return { error: 'Todos os campos devem ser preenchidos!' };
      };

      for (const invite of this.state.invitations) {
        if(invite.email.length >= 1) {
          const regexEmail = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
          const resultRegexEmail = regexEmail.test(invite.email);
          if(!resultRegexEmail) {
            return { error: 'Insira um e-mail válido!' }
          }
        }
      }

      const nonDuplicateEmails = new Set();
      const hasDuplicateEmails = this.state.invitations.some(invite => {
        if (nonDuplicateEmails.has(invite.email)) return true;
        nonDuplicateEmails.add(invite.email);
        return false;
      })
      if(hasDuplicateEmails) return { error: 'Insira um e-mail único para cada convite!' }

      return { error: false };
    },

    inviteHasErrorsByIndex (index) {
      const invite = this.state.invitations[index];

      const newIndex = this.state.invitations.filter((invitation) => invitation.email.length)
        .findIndex((invitation) => invitation.email === invite.email);

      if (newIndex === -1) {
        return false;
      }

      return this.hasErrorsByKey(`invitations.${newIndex}.email`);
    },

    getInviteErrorsByIndex (index) {
      const invite = this.state.invitations[index];

      const newIndex = this.state.invitations.filter((invitation) => invitation.email.length)
        .findIndex((invitation) => invitation.email === invite.email);

      if (newIndex === -1) {
        return [];
      }

      return this.getErrorsByKey(`invitations.${newIndex}.email`);
    },
  }
}
</script>

<style lang="scss" scoped>
@import '../../../sass/variables';
@import '../../../sass/mixins';

.few-more-information-content {
  width: 100%;
  display: flex;
  flex-direction: column;

  h3 {
    color: $salesflow-font-gray;
    font-family: 'Inter';
    font-size: 20px;
    font-weight: 500;
    line-height: 150%;
    max-width: 430px;
  }

  .few-more-information-form {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 1.5rem;

    .few-more-information-form-group {
      display: flex;
      flex-direction: column;

      label {
        @include salesflow-text();
        font-family: 'Inter';
        margin-bottom: 0.5rem;
        margin-left: 1rem;
        font-size: 12px;
        line-height: 75%;
      }

      ::v-deep {
        button.dropdown-toggle {
          width: 100%;

          .btn {
            border: 1px solid $salesflow-outline-gray-light;
            padding: 0.625rem 0.75rem;
            border-radius: 12px;
            height: 45px;
          }

          .value {
            @include salesflow-subtitle($color: $salesflow-font-dark);
            font-family: 'Inter';

            &.placeholder {
              @include salesflow-subtitle($color: $salesflow-font-gray-light);
              font-family: 'Inter';
            }
          }
        }

        .dropdown-menu {
          top: 8px !important;
          max-height: 338px;
          border-color: $salesflow-outline-gray-light;
        }

        .dropdown-item:hover {
          background-color: $salesflow-purple-extra-light;
        }
      }
    }
  }
}

@media screen and (min-width: 744px) {
  .few-more-information-content {
    h3 {
      font-size: 24px;
    }
  }
}

@media (min-width: 744px) and (max-height: 540px) {
  h3 {
    font-size: 20px !important;
  }
}

.text-input {
  font-family: 'Inter';
  font-size: 16px;
  $color: $salesflow-font-dark;
  border: 1px solid #F0F0F0;
  border-radius: 12px;
  padding: 0.625rem 0.75rem;
  height: 45px;
}

.text-input:focus {
  border: 2px solid $salesflow-primary;
}

.button-group {
  width: 100%;

  .button {
    width: 100%;
    text-align: center;
    font-family: 'Inter';
    border: 1px solid #F0F0F0;
    background-color: #FFF;
    cursor: pointer;
    height: 45px;
  }
  .button.first {
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
  }
  .button.last {
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
  }
  .button:not(.last) {
    border-right: none;
  }
  .button.active {
    background-color: #5F33FF;
    color: white;
    border-color: #5F33FF;
  }
}

.invite-content {
  h4 {
    color: $salesflow-font-dark;
    font-family: 'Inter';
    font-size: 16px;
    font-weight: 600;
  }

  h5 {
    font-family: 'Inter';
    color: $salesflow-font-gray;
    font-size: 12px;
  }

  .invite-group {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: 8px;

    input {
      border: 1px solid #F0F0F0;
      border-radius: 12px;
      padding: 0.625rem 0.75rem;
      height: 45px;
      width: 100%;
    }

    input::placeholder {
      font-size: 16px;
      font-family: 'Inter';
    }

    input:focus {
      border: 2px solid $salesflow-primary;
    }
  }

  .invite-group:not(:last-child) {
    margin-bottom: .5rem;
  }

  span {
    color: $salesflow-primary;
    font-size: 12px;
    cursor: pointer;
  }

  span:hover {
    color: $salesflow-primary-hover;
  }

  ::v-deep {
    .btn {
      padding: 0.625rem 0.75rem;
      border-color: $salesflow-outline-gray-light;
      border-radius: 12px;
      height: 45px;
    }
    .dropdown-menu {
      top: 8px !important;
      border-color: $salesflow-outline-gray-light;
    }
    .dropdown-item:hover {
      background-color: $salesflow-purple-extra-light;
    }
    .value {
      @include salesflow-subtitle($color: $salesflow-font-dark);
      font-family: 'Inter';
      font-size: 16px;
    }
  }
}

button.salesflow-btn {
  font-family: 'Inter';
  font-weight: 400 !important;
  line-height: 1rem;
  padding: 1rem 2rem !important;
  height: 45px;
}
</style>
